import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useWizardStore, type InstallTier } from '../store/wizardStore';
import { detectMode, emit } from '../components/ShopifyBridge';
import { StepperNav } from '../components/StepperNav';
import { formatPrice } from '../services/pricing';

interface RecipeInstallComp {
  sku: string; name: string; qty: number;
  priceEurExVat: number | null; priceEurIncVat: number | null; specialty: string;
}

interface RecipeItem {
  divisionId: string;
  divisionLabel: string;
  components: Array<{ sku: string; name: string; qty: number; priceEur: number; priceEurExVat: number; installMinutes: number }>;
  divisionTotal: number;
  divisionMinutes: number;
  installComponents: RecipeInstallComp[];
  installTotal: number;
}

interface Recipe {
  sessionId: string;
  installTier: InstallTier;
  items: RecipeItem[];
  travelFee: {
    km: number | null;
    baseKm: number;
    billableKm: number;
    kmPending: boolean;
    lines: Array<{ sku: string; name: string; qty: number; priceEurExVat: number | null; priceEurIncVat: number | null }>;
    totalExVat: number | null;
    totalIncVat: number | null;
  } | null;
  hub: {
    required: true;
    qty: number;
    recommendedSku: string;
    chosenSku: string;
    options: Array<{ sku: string; kind: 'zigbee' | 'matter'; name: string; priceEurExVat: number | null; priceEurIncVat: number | null }>;
  } | null;
  grandTotal: number;
  grandTotalExVat: number;
  grandTotalIncVat: number;
  totalInstallMinutes: number;
}

const TIER_LABELS: Record<InstallTier, { label: string; desc: string }> = {
  material:  { label: 'Apenas material',                desc: 'Fornecimento sem instalação' },
  electrica: { label: 'Material + instalação elétrica', desc: 'Cablagem e montagem dos pontos' },
  config:    { label: 'Material + configuração',        desc: 'Programação e integração' },
  full:      { label: 'Material + instalação + config', desc: 'Serviço completo chave-na-mão' },
};

interface QuoteResult {
  quoteId: string;
  moloniQuoteId: string;
  totalEur: number;
  pdfUrl: string | null;
  mock: boolean;
}

type Stage = 'review' | 'form' | 'quoted';

// Per-session access token header. Empty when there is no token yet (first
// create) — the server grandfathers that single create call.
function tokenHeader(token: string | null): Record<string, string> {
  return token ? { 'X-Session-Token': token } : {};
}

export function CartPage() {
  const navigate = useNavigate();
  const {
    sessionId, accessToken, wizardType, divisions, selections, devices,
    installTier, roomDecor, roomSurveyNote, setSessionId, setAccessToken, setInstallTier,
  } = useWizardStore();

  // #5: pre-format the EXISTING-conditions survey (markers + per-room notes)
  // for the lead. Furniture markers are visual-only and excluded; existing
  // points/devices are advisory context for quoting (sujeito a visita técnica).
  function buildSpaceSurvey(): string | undefined {
    const lines: string[] = [];
    for (const d of divisions) {
      const existing = (roomDecor[d.id] ?? []).filter((i) => i.category === 'existing');
      const note = (roomSurveyNote[d.id] ?? '').trim();
      if (existing.length === 0 && !note) continue;
      const counts = new Map<string, number>();
      for (const it of existing) counts.set(it.label, (counts.get(it.label) ?? 0) + 1);
      const parts = [...counts.entries()].map(([label, n]) => `${n}× ${label}`);
      let line = `${d.label}: ${parts.join(', ') || '(sem marcadores)'}`;
      if (note) line += ` | Nota: ${note}`;
      lines.push(line);
    }
    return lines.length ? lines.join('\n') : undefined;
  }

  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('review');
  // P0-3: customer's hub choice (Zigbee vs Matter). null → server recommends.
  const [hubSku, setHubSku] = useState<string | null>(null);

  // Quote form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nif, setNif] = useState('');
  const [address, setAddress] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  // Deslocação: client postal code (CP7 XXXX-XXX) → one-way distance to the store.
  const [postalCode, setPostalCode] = useState('');
  // Debounced copy that actually drives the recipe fetch (avoid a call per keystroke).
  const [postalCodeQ, setPostalCodeQ] = useState('');

  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [billingMsg, setBillingMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Ensure a backend session exists & mirrors local selections (once on mount).
  useEffect(() => {
    let cancelled = false;
    async function syncSession() {
      try {
        const payload = { wizardType, divisions, selections, devices };
        if (!sessionId) {
          const res = await fetch('/api/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const json = (await res.json()) as { success: boolean; data?: { id: string; accessToken?: string } };
          if (!json.success || !json.data) throw new Error('create-session');
          if (!cancelled) {
            setSessionId(json.data.id);
            setAccessToken(json.data.accessToken ?? null);
          }
        } else {
          await fetch(`/api/sessions/${sessionId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...tokenHeader(accessToken) },
            body: JSON.stringify(payload),
          });
        }
      } catch {
        if (!cancelled) { setError('Não foi possível calcular o orçamento. Tenta novamente.'); setLoading(false); }
      }
    }
    void syncSession();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the postal code (only a complete CP7 XXXX-XXX drives a refetch).
  useEffect(() => {
    const cp = postalCode.trim();
    const t = setTimeout(() => setPostalCodeQ(/^\d{4}-\d{3}$/.test(cp) ? cp : ''), 500);
    return () => clearTimeout(t);
  }, [postalCode]);

  // Load the server-computed recipe (single source of truth for totals).
  // Re-runs when the session, the service tier, the hub choice, or the postal
  // code changes — so switching tier (P0-5), hub (P0-3) or entering the postal
  // code (deslocação) updates the prices live.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const hubParam = hubSku ? `&hub=${encodeURIComponent(hubSku)}` : '';
        const cpParam = postalCodeQ ? `&postalCode=${encodeURIComponent(postalCodeQ)}` : '';
        const rr = await fetch(
          `/api/sessions/${sessionId}/recipe?tier=${installTier}${hubParam}${cpParam}`,
          { headers: tokenHeader(accessToken) },
        );
        const rj = (await rr.json()) as { success: boolean; data?: Recipe };
        if (!rj.success || !rj.data) throw new Error('recipe');
        if (!cancelled) setRecipe(rj.data);
      } catch {
        if (!cancelled) setError('Não foi possível calcular o orçamento. Tenta novamente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, installTier, hubSku, postalCodeQ]);

  const totalComponents = recipe?.items.reduce((s, i) => s + i.components.length, 0) ?? 0;
  // Anything configured locally (combos or standalone) — drives the empty state
  // when the server recipe is degraded and returns no component lines.
  const hasAnyLocalItems =
    Object.values(devices).some((cs) => cs.length > 0) ||
    Object.values(selections).some((skus) => skus.length > 0);

  // Returns the earliest selectable install date: 5 business days from today.
  // Weekends are skipped; Portuguese public holidays are NOT checked here (lead-time handles it).
  function getMinInstallDate(): string {
    const d = new Date();
    let businessDays = 0;
    while (businessDays < 5) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) businessDays++;
    }
    return d.toISOString().slice(0, 10);
  }

  // Returns true for dates that should be disabled: weekends or before minDate.
  function isDateDisabled(dateStr: string): boolean {
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();
    return dow === 0 || dow === 6;
  }

  const submitQuote = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!sessionId) return;
      setSubmitting(true);
      setActionError(null);
      try {
        const res = await fetch('/api/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...tokenHeader(accessToken) },
          body: JSON.stringify({
            sessionId,
            customer: { name, email, nif: nif || undefined, address },
            installTier,
            scheduledDate: installTier !== 'material' && scheduledDate ? scheduledDate : undefined,
            spaceSurvey: buildSpaceSurvey(),
            hubSku: hubSku ?? recipe?.hub?.chosenSku ?? undefined,
            postalCode: postalCodeQ || undefined,
          }),
        });
        const json = (await res.json()) as { success: boolean; data?: QuoteResult; message?: string };
        if (!json.success || !json.data) {
          setActionError(json.message ?? 'Erro ao gerar orçamento.');
          return;
        }
        setQuote(json.data);
        setStage('quoted');
      } catch {
        setActionError('Sem ligação ao servidor.');
      } finally {
        setSubmitting(false);
      }
    },
    [sessionId, accessToken, name, email, nif, address, installTier, scheduledDate, hubSku, recipe],
  );

  const pay = useCallback(async () => {
    if (!sessionId || !quote) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...tokenHeader(accessToken) },
        body: JSON.stringify({ sessionId, quoteId: quote.quoteId, installTier, hubSku: hubSku ?? recipe?.hub?.chosenSku ?? undefined, postalCode: postalCodeQ || undefined }),
      });
      const json = (await res.json()) as {
        success: boolean;
        data?: { cartId: string; checkoutUrl: string };
        message?: string;
      };
      if (!json.success || !json.data) {
        setActionError(json.message ?? 'Erro ao criar carrinho Shopify.');
        return;
      }
      const { checkoutUrl } = json.data;
      // Standalone → same-tab redirect. Iframed → break out to _top so the
      // Shopify checkout isn't trapped inside the embedded wizard.
      if (detectMode() === 'iframe') {
        emit({ type: 'syhouse:cart-handoff', checkoutUrl });
        try {
          if (window.top) window.top.location.href = checkoutUrl;
          else window.location.assign(checkoutUrl);
        } catch {
          window.open(checkoutUrl, '_top');
        }
      } else {
        window.location.assign(checkoutUrl);
      }
    } catch {
      setActionError('Sem ligação ao servidor.');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, accessToken, quote, installTier, hubSku, recipe]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <StepperNav current={4} />
      <div className="mb-8 mt-4">
        <button
          type="button"
          onClick={() => navigate(`/wizard/${wizardType}/cor`)}
          className="mb-4 text-sm text-smartify-muted hover:text-smartify-ink"
        >
          ← Voltar
        </button>
        <h1 className="text-xl font-bold text-smartify-ink">Resumo da configuração</h1>
        <p className="mt-1 text-sm text-smartify-muted">
          Revisão da tua selecção · {wizardType}
        </p>
      </div>

      {loading && (
        <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center text-sm text-smartify-muted">
          A calcular orçamento…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-smartify border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {recipe && !loading && !error && (
        <>
          {recipe.items.length === 0 || (totalComponents === 0 && !hasAnyLocalItems) ? (
            <div className="rounded-smartify border border-smartify-line bg-white p-8 text-center">
              <p className="text-sm text-smartify-muted">
                Nenhum componente seleccionado ainda.
              </p>
              <button
                type="button"
                onClick={() => navigate(`/wizard/${wizardType}`)}
                className="mt-4 rounded-smartify bg-smartify-brand px-6 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Configurar divisões
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {recipe.items.map((item) => {
                // When the server catalog is degraded the recipe comes back
                // without component lines — fall back to what the customer
                // actually configured locally so the review is never blank.
                const localCombos = devices[item.divisionId] ?? [];
                const localComboSkus = new Set(localCombos.flatMap((c) => c.skus));
                const localStandalone = (selections[item.divisionId] ?? []).filter((s) => !localComboSkus.has(s));
                const serverEmpty = item.components.length === 0 && item.installComponents.length === 0;
                const hasLocal = localCombos.length > 0 || localStandalone.length > 0;
                // Unconfigured division → no card (was an empty white box).
                if (serverEmpty && !hasLocal) return null;
                if (serverEmpty) {
                  return (
                    <div key={item.divisionId} className="rounded-smartify border border-smartify-line bg-white p-4">
                      <h3 className="mb-3 font-semibold text-smartify-ink">{item.divisionLabel}</h3>
                      <ul className="space-y-1">
                        {localCombos.map((c) => (
                          <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="min-w-0 text-smartify-ink">
                              {c.label}{(c.qty ?? 1) > 1 ? ` ×${c.qty}` : ''}
                            </span>
                          </li>
                        ))}
                        {localStandalone.map((sku) => (
                          <li key={sku} className="text-sm text-smartify-ink">{sku}</li>
                        ))}
                      </ul>
                      <p className="mt-2 text-[11px] text-smartify-muted">
                        Detalhe e preços destas linhas são confirmados no orçamento final.
                      </p>
                    </div>
                  );
                }
                return (
                <div key={item.divisionId} className="rounded-smartify border border-smartify-line bg-white p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-semibold text-smartify-ink">{item.divisionLabel}</h3>
                    {item.divisionTotal > 0 && (
                      <span className="text-sm font-medium text-smartify-ink">
                        {formatPrice(item.divisionTotal)}
                      </span>
                    )}
                  </div>
                  <ul className="space-y-1">
                    {item.components.map((comp) => (
                      <li key={comp.sku} className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 text-smartify-ink">
                          {comp.name}{comp.qty > 1 ? ` ×${comp.qty}` : ''}
                        </span>
                        <span className="shrink-0 text-right text-xs text-smartify-muted">
                          {comp.priceEurExVat > 0 ? (
                            <>{formatPrice(comp.priceEurExVat)} <span className="opacity-60">s/IVA</span></>
                          ) : 'sob consulta'}
                        </span>
                      </li>
                    ))}
                    {item.installComponents.map((comp) => (
                      <li key={comp.sku} className="flex items-center justify-between gap-3 text-sm border-t border-smartify-line/50 pt-1 mt-1">
                        <span className="min-w-0 text-smartify-ink/70 italic">
                          {comp.name} ×{comp.qty}
                        </span>
                        <span className="shrink-0 text-right text-xs text-smartify-muted">
                          {comp.priceEurExVat != null ? (
                            <>{formatPrice(comp.priceEurExVat * comp.qty)} <span className="opacity-60">s/IVA</span></>
                          ) : 'sob consulta'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                );
              })}

              {/* P0-3: required hub — let the customer choose Zigbee vs Matter */}
              {recipe.hub && (
                <div className="rounded-smartify border border-smartify-line bg-white p-4">
                  <p className="text-sm font-semibold text-smartify-ink">Central necessária (hub)</p>
                  <p className="mt-0.5 mb-3 text-xs text-smartify-muted">
                    Os teus módulos Zigbee precisam de uma central para comunicar. Compras uma vez e
                    serve a casa toda. Escolhe a tecnologia:
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {recipe.hub.options.map((o) => {
                      const selected = (hubSku ?? recipe.hub!.chosenSku) === o.sku;
                      const recommended = recipe.hub!.recommendedSku === o.sku;
                      return (
                        <button
                          key={o.sku}
                          type="button"
                          onClick={() => setHubSku(o.sku)}
                          className={[
                            'rounded-smartify border p-3 text-left transition-colors',
                            selected
                              ? 'border-smartify-brand bg-smartify-brand/5 ring-1 ring-smartify-brand'
                              : 'border-smartify-line hover:border-smartify-brand/40',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-smartify-ink">
                              {o.kind === 'matter' ? 'Hub Matter' : 'Hub Zigbee'}
                            </span>
                            {recommended && (
                              <span className="rounded-full bg-smartify-brand/10 px-1.5 py-px text-[10px] font-semibold text-smartify-brand">
                                Recomendado
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-smartify-muted">
                            {o.priceEurIncVat != null ? formatPrice(o.priceEurIncVat) : 'sob consulta'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] text-smartify-muted">
                    Matter é mais universal (funciona com mais ecossistemas); Zigbee é a opção nativa
                    destes módulos.
                  </p>
                </div>
              )}

              {/* Deslocação — postal code drives the one-way distance (PRO-001 +
                  PRO-003×km). Only relevant when there is installation. */}
              {recipe.installTier !== 'material' && (
                <div className="rounded-smartify border border-smartify-line bg-white p-3">
                  <label htmlFor="quote-postal" className="block text-sm font-medium text-smartify-ink">
                    Código postal (para calcular a deslocação)
                  </label>
                  <input
                    id="quote-postal"
                    type="text"
                    inputMode="numeric"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="ex. 2675-303"
                    maxLength={8}
                    className="mt-1 w-40 rounded-smartify border border-smartify-line px-3 py-2 text-sm text-smartify-ink focus:border-smartify-brand focus:outline-none"
                  />
                  <p className="mt-1 text-[11px] text-smartify-muted">
                    A visita (até {recipe.travelFee?.baseKm ?? 25} km) está incluída; acima disso soma-se
                    por km. Distância estimada — a nossa equipa confirma antes de avançar.
                  </p>
                </div>
              )}

              {/* Price summary — always shows ex-VAT + inc-VAT */}
              <div className="rounded-smartify border border-smartify-line bg-smartify-paper p-4 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-smartify-muted">Subtotal (sem IVA)</span>
                  <span className="font-medium text-smartify-ink">
                    {recipe.grandTotalExVat > 0 ? formatPrice(recipe.grandTotalExVat) : 'sob consulta'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-smartify-muted">
                  <span>IVA 23%</span>
                  <span>
                    {recipe.grandTotalIncVat > 0 && recipe.grandTotalExVat > 0
                      ? formatPrice(Math.round((recipe.grandTotalIncVat - recipe.grandTotalExVat) * 100) / 100)
                      : 'sob consulta'}
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between border-t border-smartify-line pt-2">
                  <span className="font-semibold text-smartify-ink">Total (com IVA)</span>
                  <span className="text-lg font-bold text-smartify-brand">
                    {recipe.grandTotalIncVat > 0 ? formatPrice(recipe.grandTotalIncVat) : 'sob consulta'}
                  </span>
                </div>
                {recipe.travelFee && (
                  <div className="pt-1">
                    <div className="flex items-center justify-between text-xs text-smartify-ink">
                      <span>↳ deslocação{recipe.travelFee.km != null ? ` (~${recipe.travelFee.km} km, só ida)` : ''}</span>
                      <span className="text-smartify-muted">
                        {recipe.travelFee.totalIncVat != null ? formatPrice(recipe.travelFee.totalIncVat) : 'sob consulta'}
                      </span>
                    </div>
                    {recipe.travelFee.lines.map((l) => (
                      <div key={l.sku} className="flex items-center justify-between pl-3 text-[11px] text-smartify-muted">
                        <span>{l.name}{l.qty > 1 ? ` × ${l.qty}` : ''}</span>
                        <span>{l.priceEurIncVat != null ? formatPrice(Math.round(l.priceEurIncVat * l.qty * 100) / 100) : 'sob consulta'}</span>
                      </div>
                    ))}
                    {recipe.travelFee.kmPending && (
                      <div className="pl-3 pt-0.5 text-[11px] text-amber-700">
                        {postalCodeQ
                          ? 'Não conseguimos calcular a distância para este código postal — a nossa equipa confirma no orçamento.'
                          : 'Introduz o teu código postal para calcular a deslocação por km.'}
                      </div>
                    )}
                  </div>
                )}
                {recipe.hub && (() => {
                  const chosen = recipe.hub.options.find((o) => o.sku === recipe.hub!.chosenSku);
                  return (
                    <div className="flex items-center justify-between text-xs pt-1">
                      <span className="text-smartify-ink">↳ inclui {chosen?.name ?? 'Hub'}</span>
                      <span className="text-smartify-muted">
                        {chosen?.priceEurIncVat != null ? formatPrice(chosen.priceEurIncVat) : 'sob consulta'}
                      </span>
                    </div>
                  );
                })()}
                {recipe.totalInstallMinutes > 0 && (
                  <div className="flex items-center justify-between text-xs text-smartify-muted">
                    <span>Tempo estimado de instalação</span>
                    <span>{Math.ceil(recipe.totalInstallMinutes / 60)}h</span>
                  </div>
                )}
              </div>

              {/* Stage: review → choose service level early (P0-5) + "Pedir orçamento" */}
              {stage === 'review' && (
                <div className="rounded-smartify border border-smartify-line bg-white p-4">
                  <p className="mb-2 text-sm font-semibold text-smartify-ink">Tipo de serviço</p>
                  <p className="mb-3 text-xs text-smartify-muted">
                    Escolhe já como queres avançar — podes mudar antes de pedir o orçamento.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.entries(TIER_LABELS) as [InstallTier, { label: string; desc: string }][]).map(([tier, { label }]) => (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => { setInstallTier(tier); if (tier === 'material') setScheduledDate(''); }}
                        className={[
                          'rounded-smartify border px-3 py-2 text-left text-xs font-medium transition-colors',
                          installTier === tier
                            ? 'border-smartify-brand bg-smartify-brand/5 text-smartify-ink'
                            : 'border-smartify-line text-smartify-ink hover:border-smartify-brand/40',
                        ].join(' ')}
                      >
                        {label}
                        {tier === 'material' && <span className="ml-1 text-green-700">· 0€ instalação</span>}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStage('form')}
                    className="mt-4 w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90"
                  >
                    Pedir orçamento
                  </button>
                </div>
              )}

              {/* Stage: form */}
              {stage === 'form' && (
                <form
                  onSubmit={submitQuote}
                  className="space-y-3 rounded-smartify border border-smartify-line bg-white p-4"
                >
                  <h3 className="font-semibold text-smartify-ink">Os teus dados</h3>
                  <div>
                    <label htmlFor="quote-name" className="mb-1 block text-xs text-smartify-muted">Nome</label>
                    <input
                      id="quote-name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-email" className="mb-1 block text-xs text-smartify-muted">Email</label>
                    <input
                      id="quote-email"
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-nif" className="mb-1 block text-xs text-smartify-muted">
                      NIF <span className="opacity-60">(opcional)</span>
                    </label>
                    <input
                      id="quote-nif"
                      value={nif}
                      onChange={(e) => setNif(e.target.value)}
                      className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-address" className="mb-1 block text-xs text-smartify-muted">Morada</label>
                    <input
                      id="quote-address"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs font-semibold text-smartify-muted">Tipo de serviço</p>
                    <div className="space-y-1.5">
                      {(Object.entries(TIER_LABELS) as [InstallTier, { label: string; desc: string }][]).map(([tier, { label, desc }]) => (
                        <label
                          key={tier}
                          className={[
                            'flex cursor-pointer items-start gap-2.5 rounded-smartify border p-2.5 transition-colors',
                            installTier === tier
                              ? 'border-smartify-brand bg-smartify-brand/5'
                              : 'border-smartify-line hover:border-smartify-brand/40',
                          ].join(' ')}
                        >
                          <input
                            type="radio"
                            name="installTier"
                            value={tier}
                            checked={installTier === tier}
                            onChange={() => { setInstallTier(tier); if (tier === 'material') setScheduledDate(''); }}
                            className="mt-0.5 h-4 w-4 shrink-0 border-smartify-line text-smartify-brand focus:ring-smartify-brand"
                          />
                          <div>
                            <p className="text-sm font-medium text-smartify-ink">{label}</p>
                            <p className="text-xs text-smartify-muted">{desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {installTier !== 'material' && (
                    <div>
                      <label htmlFor="quote-date" className="mb-1 block text-xs text-smartify-muted">
                        Data preferida de instalação
                        <span className="ml-1 opacity-60">(mín. 5 dias úteis)</span>
                      </label>
                      <input
                        id="quote-date"
                        type="date"
                        value={scheduledDate}
                        min={getMinInstallDate()}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val || !isDateDisabled(val)) setScheduledDate(val);
                        }}
                        className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm"
                      />
                      {scheduledDate && isDateDisabled(scheduledDate) && (
                        <p className="mt-1 text-xs text-red-600">
                          Fim-de-semana não disponível. Escolhe um dia útil.
                        </p>
                      )}
                      {!scheduledDate && (
                        <p className="mt-1 text-xs text-smartify-muted">
                          Deixa em branco para ser contactado para agendamento.
                        </p>
                      )}
                    </div>
                  )}

                  {actionError && (
                    <div className="rounded-smartify border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                      {actionError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-50"
                  >
                    {submitting ? 'A gerar orçamento…' : 'Gerar orçamento'}
                  </button>
                </form>
              )}

              {/* Stage: quoted */}
              {stage === 'quoted' && quote && (
                <div className="space-y-4">
                  {/* Success header */}
                  <div className="rounded-smartify border border-green-200 bg-green-50 p-4 text-center">
                    <p className="text-3xl">✅</p>
                    <h3 className="mt-1 font-semibold text-green-800">Pedido recebido!</h3>
                    <p className="mt-0.5 text-sm text-green-700">
                      Entraremos em contacto em breve.
                    </p>
                  </div>

                  {/* SIMULAÇÃO — prominent amber card */}
                  {quote.mock && (
                    <div className="rounded-smartify border-2 border-amber-300 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl leading-none">⚠️</span>
                        <div>
                          <p className="font-semibold text-amber-800">
                            Orçamento em modo SIMULAÇÃO
                          </p>
                          <p className="mt-1 text-sm text-amber-700">
                            A integração com o sistema de faturação está a ser preparada.
                            A equipa Smartify irá enviar-te o orçamento real por email
                            em até <strong>24 horas úteis</strong>.
                          </p>
                          <p className="mt-2 text-xs text-amber-600">
                            Ref. interna:{' '}
                            <span className="font-mono">{quote.moloniQuoteId}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quote details */}
                  <div className="rounded-smartify border border-smartify-line bg-white p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-smartify-muted">Total estimado</span>
                      <span className="text-lg font-bold text-smartify-brand">
                        {formatPrice(quote.totalEur)}
                      </span>
                    </div>
                    {!quote.mock && quote.pdfUrl && (
                      <a
                        href={quote.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-sm text-smartify-accent underline"
                      >
                        Ver PDF do orçamento
                      </a>
                    )}
                  </div>

                  {actionError && (
                    <div className="rounded-smartify border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                      {actionError}
                    </div>
                  )}

                  {!quote.mock && (
                    <button
                      type="button"
                      onClick={pay}
                      disabled={submitting}
                      className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {submitting ? 'A preparar checkout…' : 'Pagar agora'}
                    </button>
                  )}

                  {/* P0-1: schedule the installation. Only for tiers that
                      include installation — "Apenas material" has nothing to
                      schedule. Reached after payment; carries the quote ref. */}
                  {installTier !== 'material' && (
                    <button
                      type="button"
                      onClick={() => navigate(`/agenda?ref=${encodeURIComponent(quote.moloniQuoteId || quote.quoteId)}`)}
                      className="w-full rounded-smartify border border-smartify-brand bg-smartify-brand/5 px-6 py-3 font-semibold text-smartify-brand hover:bg-smartify-brand/10"
                    >
                      Agendar instalação →
                    </button>
                  )}

                  {/* Q36: EuPago billing path (prepared). Returns a Multibanco
                      reference when configured, else "cobrança manual". */}
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={async () => {
                      if (!quote) return;
                      setBillingMsg(null); setSubmitting(true);
                      try {
                        const res = await fetch('/api/billing/eupago', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ quoteId: quote.quoteId, method: 'multibanco' }),
                        });
                        const j = (await res.json()) as { success: boolean; data?: { configured?: boolean; entity?: string; mbReference?: string; amountEur?: number; message?: string }; message?: string };
                        const d = j.data;
                        setBillingMsg(
                          j.success && d?.configured && d.mbReference
                            ? `Referência Multibanco — Entidade ${d.entity} · Ref ${d.mbReference} · ${formatPrice(d.amountEur ?? quote.totalEur)}`
                            : (d?.message ?? j.message ?? 'Cobrança manual — a nossa equipa contacta-te.'),
                        );
                      } catch {
                        setBillingMsg('Sem ligação ao servidor.');
                      } finally { setSubmitting(false); }
                    }}
                    className="w-full rounded-smartify border border-smartify-line px-6 py-3 text-sm font-medium text-smartify-ink hover:border-smartify-brand disabled:opacity-50"
                  >
                    Avançar para cobrança (referência de pagamento)
                  </button>
                  {billingMsg && (
                    <div className="rounded-smartify border border-smartify-line bg-smartify-paper p-3 text-xs text-smartify-ink">
                      {billingMsg}
                    </div>
                  )}

                  <p className="text-center text-xs text-smartify-muted">
                    {quote.mock
                      ? 'O teu orçamento real chegará por email nas próximas 24 horas úteis.'
                      : 'Serás redirecionado para o checkout seguro Shopify.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
